"use client";

type PaymentStatus = "success" | "fail";
type PaymentPayload = { type: PaymentStatus; ts: number };
const CHANNEL = "payment-close";
const STORAGE_KEY = "payment-close:last";

let bc: BroadcastChannel | null = null;
function getChannel() {
  if (typeof window === "undefined") return null;
  if (!bc) {
    try {
      bc = new BroadcastChannel(CHANNEL);
    } catch {
      bc = null;
    }
  }
  return bc;
}

export function notifyPayment(status: PaymentStatus) {
  const payload = { type: status as PaymentStatus, ts: Date.now() };
  getChannel()?.postMessage(payload);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {}
}

function parsePaymentPayload(raw: string | null): PaymentPayload | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<PaymentPayload>;
    if (
      (parsed.type === "success" || parsed.type === "fail") &&
      typeof parsed.ts === "number"
    ) {
      return { type: parsed.type, ts: parsed.ts };
    }
  } catch {}
  return null;
}

export function onPayment(
  handler: (status: PaymentStatus) => void,
  signal?: AbortSignal,
  recentMs = 2 * 60 * 1000,
) {
  const ch = getChannel();
  let lastHandledTs = 0;
  let cleaned = false;

  const notifyIfFresh = (status: PaymentStatus, ts: number) => {
    if (Date.now() - ts > recentMs || ts <= lastHandledTs) {
      return;
    }
    lastHandledTs = ts;
    handler(status);
  };

  const onBCMessage = (ev: MessageEvent) => {
    const data = ev?.data as Partial<PaymentPayload> | undefined;
    if (
      (data?.type === "success" || data?.type === "fail") &&
      typeof data.ts === "number"
    ) {
      notifyIfFresh(data.type, data.ts);
    }
  };

  const onStorage = (ev: StorageEvent) => {
    if (ev.key !== STORAGE_KEY) return;
    const payload = parsePaymentPayload(ev.newValue);
    if (payload) {
      notifyIfFresh(payload.type, payload.ts);
    }
  };

  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    ch?.removeEventListener?.("message", onBCMessage as any);
    window.removeEventListener("storage", onStorage);
    signal?.removeEventListener("abort", cleanup);
  };

  ch?.addEventListener?.("message", onBCMessage as any);
  window.addEventListener("storage", onStorage);
  signal?.addEventListener("abort", cleanup);

  const payload = parsePaymentPayload(localStorage.getItem(STORAGE_KEY));
  if (payload) {
    queueMicrotask(() => notifyIfFresh(payload.type, payload.ts));
  }

  return cleanup;
}

export function onPaymentOnce(
  handler: (status: PaymentStatus) => void,
  signal?: AbortSignal,
  recentMs = 2 * 60 * 1000,
) {
  let cleanup: (() => void) | null = null;
  cleanup = onPayment(
    (status) => {
      handler(status);
      cleanup?.();
    },
    signal,
    recentMs,
  );
  return () => cleanup?.();
}
import { useSearchParams } from "next/navigation";
import { ReactNode, useCallback, useEffect } from "react";
import { FullScreen } from "./FullScreen";
import { Loader } from "./Loader";

export function PaymentClose() {
  const params = useSearchParams();
  const result = params.get("payment");

  useEffect(() => {
    if (result === "success" || result === "fail") {
      notifyPayment(result);
      const t = setTimeout(() => {
        try {
          window.close();
        } catch {}
      }, 600);
      return () => clearTimeout(t);
    }
  }, [result]);

  if (result === "success" || result === "fail") {
    const wrap = useCallback(
      ({ children }: { children: ReactNode }) => (
        <div className="absolute top-0 left-0 w-full h-full bg-white">
          {children}
        </div>
      ),
      [],
    );
    return (
      <FullScreen wrap={wrap}>
        <Loader action="Waiting for window to close (if this window does not close, feel free to close it)" />
      </FullScreen>
    );
  }
  return null;
}
